import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Contexto } from "@/hooks/useTelecomAccess";
import { useRolesEfectivos } from "@/lib/sesion";

/**
 * Roles internos del OC con acceso al módulo Seguridad. El sector Seguridad es
 * una solución temporal sobre el flujo de Telecom, por eso comparte los roles
 * operativos (RESPONSABLE_TELECOM, EVALUADOR_TECNICO, etc.). Mantener
 * sincronizada con {@code IamApi.ROLES_INTERNOS_NPMX}.
 */
const ROLES_INTERNOS = new Set([
  "ADMIN_SICEN",
  "GERENTE_CALIDAD",
  "AUXILIAR_CALIDAD",
  "RESPONSABLE_TELECOM",
  "RESPONSABLE_ELECTRICA",
  "RESPONSABLE_EFICIENCIA",
  "RESPONSABLE_CERTIFICADOS",
  "EVALUADOR_TECNICO",
  "COORDINADOR_TECNICO",
  "AUDITOR_INTERNO",
  "DIRECCION",
  "VIGILANCIA",
  "FACTURACION",
]);


interface AccessResult {
  allowed: boolean;
  loading: boolean;
  esInterno: boolean;
  titularRazonSocial: string | null;
  sinContexto: boolean;
}

/**
 * Determina si el usuario actual puede entrar al módulo Seguridad:
 *  - Internos del OC: siempre sí.
 *  - Clientes: solo si el contexto activo (sessionStorage) tiene el sector
 *    "SEGURIDAD" en sus `sectoresCodigos`.
 *
 * Reutiliza el query `["contextos-mios"]` que ya cachea el ContextSelector.
 */
export function useSeguridadAccess(): AccessResult {
  const roles = useRolesEfectivos();
  const esInterno = roles.some((r) => ROLES_INTERNOS.has(r));

  const q = useQuery<Contexto[]>({
    queryKey: ["contextos-mios"],
    queryFn: async () => (await api.get<Contexto[]>("/api/contextos/mios")).data,
    enabled: !esInterno,
    staleTime: 30_000,
  });

  if (esInterno) {
    return { allowed: true, loading: false, esInterno: true, titularRazonSocial: null, sinContexto: false };
  }
  if (q.isLoading) {
    return { allowed: false, loading: true, esInterno: false, titularRazonSocial: null, sinContexto: false };
  }

  const titularId = window.sessionStorage.getItem("sicen.contexto.titularId");
  const activo = (q.data ?? []).find((c) => c.titularId === titularId) ?? q.data?.[0] ?? null;
  const allowed = !!activo?.sectoresCodigos?.includes("SEGURIDAD");

  return {
    allowed,
    loading: false,
    esInterno: false,
    titularRazonSocial: activo?.titularRazonSocial ?? null,
    sinContexto: !activo,
  };
}
